import camelcase from 'camelcase';
import classNames from 'classnames';
import {
  add,
  endOfDay,
  isEqual,
  startOfDay,
  startOfToday,
} from 'date-fns';
import {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Field, useForm, useFormState,
} from 'react-final-form';
import { TripCartFormState } from 'src/listing/types';
import {
  getDurationDigits, priceIsDay,
} from 'src/listing/helper';
import BookingMotivator from 'src/common/components/BookingMotivator';
import Datepicker from 'src/common/components/Datepicker';
import newTZDate, {
  formatDate, getDaysUntilInstabook, todayDate,
} from 'src/common/utils/dateHelpers';
import getClassNames from 'src/common/utils/getClassNames';
import { FORM_SELECTED_DATES } from 'src/listing/constants';
import useBookingMotivatorsContext from 'src/common/providers/useBookingMotivators';
import TripCartDuration from 'src/trip-cart-deprecated/components/TripCartDuration';
import { useTripCartContext } from 'src/trip-cart-deprecated/hooks/useTripCartContext';
import { useTripCartOptionsContext } from 'src/trip-cart-deprecated/hooks/useTripCartOptionsContext';
import DayContent from 'src/common/components/DayContent/DayContent';
import dayContentStyles from 'src/common/components/DayContent/DayContent.module.scss';
import { useListingMotivators } from 'src/listing/hooks/useListingPageMotivators';
import styles from './TripDetailsCalendar.module.scss';

export type Props = {
  inSheet?: boolean;
};

const TripCartCalendar: FC<Props> = ({
  inSheet = false,
}) => {
  const { setMotivatorsDates } = useListingMotivators();
  const {
    bookingMotivators,
    getSelectedMotivators,
    getMultiSelectedMotivators,
    changeVisibleCalendarMonth,
  } = useBookingMotivatorsContext();

  const {
    updateInstabookDatesAroundMonth,
    instabookDates,
    listing,
  } = useTripCartContext();

  const {
    blockedTimes,
    blockedTimesLoading,
    options,
    optionsLoading,
  } = useTripCartOptionsContext();

  const minDate = startOfToday();
  const form = useForm<TripCartFormState>();
  const { change } = form;
  const {
    values: {
      formSelectedDates,
      isMultiDay,
      formDuration,
    },
  } = useFormState<TripCartFormState>();

  const [selected, setSelected] = useState<Date | Date[]>(formSelectedDates);
  const start = Array.isArray(selected) ? selected[0] : selected;
  const end = selected?.[1];

  // Date is a string so we need newTZDate
  let enableSpecificDates: Date[] | undefined;

  if (options.calendar.enableSpecificDates) {
    enableSpecificDates = options.calendar.enableSpecificDates
      .map((date: string) => newTZDate(date));
  }

  const selectedMotivators = useMemo(() => {
    if (isMultiDay) {
      return getMultiSelectedMotivators(formSelectedDates);
    }
    return getSelectedMotivators(formSelectedDates);
  }, [formSelectedDates, getMultiSelectedMotivators, getSelectedMotivators, isMultiDay]);

  const isDateSelectable = useCallback((current: Date) => {
    if (!enableSpecificDates) return true;

    const formatted = formatDate(current);
    return enableSpecificDates.some((date) => formatDate(date) === formatted);
  }, [enableSpecificDates]);

  const showBookingIndicators = (date: Date) => {
    const motivators = bookingMotivators[
      formatDate(date)
    ] ?? [];
    if (!isDateSelectable(date)) {
      return classNames(
        dayContentStyles.indicator,
        dayContentStyles.disabled,
      );
    }
    return classNames(
      dayContentStyles.indicator,
      motivators.map((motivator) => dayContentStyles[camelcase(motivator)]),
    );
  };

  const changeSelectedOnForm = useCallback(async (
    date: Date | Date[] | null,
  ) => {
    if (isMultiDay || !date) {
      return;
    }
    if (Array.isArray(date)) {
      change(FORM_SELECTED_DATES, date);
      setMotivatorsDates(date);
      return;
    }

    change(FORM_SELECTED_DATES, date ? [date] : []);
    setMotivatorsDates(date ? [date] : []);
  }, [change, isMultiDay, setMotivatorsDates]);

  // For Multi-Day/Night
  const maxDays = useMemo(() => getDurationDigits(formDuration), [formDuration]);
  const isDay = useMemo(() => priceIsDay(listing.prices), [listing]);

  const changeSelectedRangeOnForm = useCallback(async (selectedRange: [Date, Date]) => {
    change(FORM_SELECTED_DATES, selectedRange);
    setMotivatorsDates(selectedRange);
  }, [change, setMotivatorsDates]);

  const changeMonth = (date: Date) => {
    changeVisibleCalendarMonth(date);
    updateInstabookDatesAroundMonth(date);
  };

  const changeSelected = useCallback((
    date: Date | Date[] | null,
    allowFormUpdate: boolean = true,
  ) => {
    if (isMultiDay || !date) return;
    const startDate = Array.isArray(date) ? date[0] : date;
    setSelected(startDate);

    if (allowFormUpdate && isDateSelectable(startDate)) {
      changeSelectedOnForm(startDate);
    }
  }, [changeSelectedOnForm, isDateSelectable, isMultiDay]);

  const changeSelectedRange = useCallback((date: Date, allowFormUpdate: boolean = true) => {
    if (!isMultiDay || !date) return;

    const range = [date, add(date, { days: isDay ? maxDays - 1 : maxDays })];

    // Ensure that we include info from the start of the day
    range[0] = startOfDay(range[0]);

    if (range[1]) {
      // Ensure that we include info until the end of the day
      range[1] = endOfDay(range[1]);
    }
    setSelected(range);
    if (allowFormUpdate) {
      changeSelectedRangeOnForm([range[0], range[1]]);
    }
  }, [changeSelectedRangeOnForm, isDay, isMultiDay, maxDays]);

  const daysUntilInstabook = getDaysUntilInstabook(selected, instabookDates);

  // For external updates
  const [previousMaxDays, setPreviousMaxDays] = useState(maxDays);

  // Update from external changes
  useEffect(
    () => {
      // Prevent early changes
      if (optionsLoading) return;

      const changedDate = Array.isArray(formSelectedDates)
        ? formSelectedDates[0]
        : formSelectedDates;

      if (
        // if range not changed
        maxDays === previousMaxDays
        // date not changed
        && isEqual(startOfDay(start), startOfDay(changedDate))
        // hasn't changed from "with end date" to "no end date" or vice-versa
        && !!end === !!formSelectedDates?.[1]) {
        return;
      }
      setPreviousMaxDays(maxDays);
      if (isMultiDay) {
        changeSelectedRange(formSelectedDates[0], false);
      } else {
        changeSelected(formSelectedDates[0], false);
      }
    },
    [
      isMultiDay,
      end,
      formSelectedDates,
      changeSelected,
      changeSelectedRange,
      start,
      maxDays,
      previousMaxDays,
      optionsLoading,
    ],
  );

  // Can choose same day or multiday
  const isMixedOrMultiDayPricing = options.isMultiday.length > 1 || isMultiDay;

  return (
    <div className={styles.root}>
      <div className={styles.calendarWrapper}>
        <div className={styles.multiDayWrapper}>
          {isMultiDay && (
            <div className={styles.duration}>
              <TripCartDuration
                hideLabel
              />
            </div>
          )}
          {inSheet && (
            <div className={getClassNames(styles, 'duration', classNames({ release1FlagEnabled: inSheet }))}>
              <TripCartDuration
                inSheet
                showMultidayToggle={false}
              />
            </div>
          )}
        </div>
        <div className={getClassNames(styles, 'calendar', classNames({ isMixedOrMultiDayPricing }))}>
          <Field name={FORM_SELECTED_DATES}>
            {() => (
              <Datepicker
                id="trip-details-calendar"
                startDate={start}
                value={start}
                endDate={end}
                inline
                focusSelectedMonth
                minDate={minDate}
                maxDate={add(todayDate(), { years: 1 })}
                monthsShown={1}
                noBorder
                classNameModifier="listingTripDetails"
                onMonthChange={changeMonth}
                onChange={changeSelected}
                selectsRange={isMultiDay}
                onSelect={changeSelectedRange}
                dayClassName={showBookingIndicators}
                renderDayContents={(day, date) => {
                  const showInstabook = !!(
                    !isMultiDay
                    && instabookDates.length > 0
                    && date
                    && instabookDates.includes(formatDate(date))
                  );

                  return (
                    <DayContent
                      showInstabook={showInstabook}
                      bookingMotivators={bookingMotivators}
                      date={date}
                      day={day}
                    />
                  );
                }}
              />
            )}
          </Field>
        </div>
        <div className={styles.bookingMotivators}>
          <BookingMotivator
            variant="compact"
            motivators={selectedMotivators}
            blockedTimes={blockedTimes}
            blockedTimesLoading={blockedTimesLoading}
            dateCount={formSelectedDates.length}
            daysUntilInstabook={daysUntilInstabook}
            anticipatedDays={listing.cancellation_anticipation}
          />
        </div>
      </div>
    </div>
  );
};

export default TripCartCalendar;
